/**
 * Tipos para Parámetros del sistema — Fase 8
 *
 * Documentos relacionados:
 *   - requerimientos.md § M14 (Configuración)
 *   - DECISIONES.md #029 (mora_coef_diario, dias_vencimiento_cupon)
 *   - DECISIONES.md #028 (pp_match_dias_dup)
 *
 * Los parámetros son globales del sistema. La pantalla solo permite EDITAR
 * los valores; nunca crear nuevos params (los crea el desarrollo via
 * migration). Tipo del valor controla la UI del input.
 */

export type TipoParametro = 'number' | 'text' | 'boolean' | 'json';

export interface Parametro {
  clave: string;
  descripcion: string | null;
  valor: string;
  tipo: TipoParametro;
  updated_at: string;
}

// ── Helpers de parsing/serialización según tipo ──────────────────────────────

/**
 * Parsea el string del valor según el tipo. Devuelve el valor tipado o un
 * objeto error si no se puede parsear.
 */
export function parseValor(valor: string, tipo: TipoParametro):
  | { ok: true; value: number | string | boolean | unknown }
  | { ok: false; error: string } {
  switch (tipo) {
    case 'number': {
      const n = Number(valor);
      if (isNaN(n)) return { ok: false, error: 'No es un número válido' };
      return { ok: true, value: n };
    }
    case 'boolean': {
      const v = valor.toLowerCase().trim();
      if (v === 'true' || v === '1') return { ok: true, value: true };
      if (v === 'false' || v === '0') return { ok: true, value: false };
      return { ok: false, error: 'Debe ser "true" o "false"' };
    }
    case 'json': {
      try {
        return { ok: true, value: JSON.parse(valor) };
      } catch {
        return { ok: false, error: 'JSON inválido' };
      }
    }
    case 'text':
    default:
      return { ok: true, value: valor };
  }
}

/**
 * Etiqueta legible del tipo (para el chip en la grilla).
 */
export function getTipoLabel(tipo: TipoParametro): string {
  const labels: Record<TipoParametro, string> = {
    number:  'Numérico',
    text:    'Texto',
    boolean: 'Booleano',
    json:    'JSON',
  };
  return labels[tipo] ?? tipo;
}

export function getTipoColor(
  tipo: TipoParametro,
): 'primary' | 'info' | 'success' | 'warning' {
  switch (tipo) {
    case 'number':  return 'primary';
    case 'text':    return 'info';
    case 'boolean': return 'success';
    case 'json':    return 'warning';
    default:        return 'info';
  }
}
